/**
 * Which of the board's controls this person gets (docs/06-UI-SPEC.md §6, SRS D4).
 *
 * The board screen belongs to the teacher and the student alike, but not all of it
 * does. Three things are the teacher's: the room code is hers to share (SRS B4), the
 * class list is hers to manage (§6.4), and the paper is part of the page, which only
 * she may change (SRS D4).
 *
 * The tools are the student's too — but only once she has turned drawing on for them.
 * A palette that quietly does nothing is worse than no palette at all.
 */

import { canDraw, type BoardAccessState, type Role } from '@opal/shared';

/** What this person can see on the board screen. */
export interface BoardChrome {
  /** Tool picker, colours, thickness, undo and redo. */
  readonly tools: boolean;
  /** The class list, with its drawing toggles. */
  readonly participants: boolean;
  /** The paper chooser. It belongs to the page, so it is the teacher's (SRS C7). */
  readonly paper: boolean;
  /** Adding, renaming, reordering and deleting pages (SRS C1, SRS D4). */
  readonly pageAdmin: boolean;
  /** The choice of layer to draw on, including the teacher-only one (SRS C8). */
  readonly layers: boolean;
  /** The room code in the header. */
  readonly classCode: boolean;
  /** SRS I1 — the board menu, and the End Class it holds. The teacher's alone. */
  readonly boardMenu: boolean;
}

export function boardChrome(role: Role, access: BoardAccessState): BoardChrome {
  const teaching = role !== 'student';
  return {
    tools: teaching || canDraw(access),
    participants: teaching,
    paper: teaching,
    pageAdmin: teaching,
    layers: teaching,
    classCode: teaching,
    boardMenu: teaching,
  };
}
